import { useEffect, useState } from "react";
import type { Api, Lead, LeadEvent, LeadStatus } from "../api";

/**
 * The lead inbox (Stage 3). Every enquiry is shown WITH its receipts — the
 * ad click, campaign, and landing page Groundbeam captured — because "which
 * ad paid for this job" is the product's promise, and the inbox is where it
 * must be visible. Status ladder + WON value are the pre-CRM slice of §M3.
 */

const STATUSES: LeadStatus[] = [
  "new",
  "contacted",
  "qualified",
  "won",
  "lost",
  "spam",
];

/** Human source from the parsed click-id, else UTM, else honest "Direct". */
export function leadSourceLabel(
  clickIdType: string | null,
  utmSource: string | null,
): string {
  switch (clickIdType) {
    case "gclid":
    case "gbraid":
    case "wbraid":
      return "Google Ads";
    case "fbclid":
      return "Meta Ads";
    case "msclkid":
      return "Microsoft Ads";
    case "ttclid":
      return "TikTok Ads";
    default:
      break;
  }
  if (utmSource) return utmSource;
  return "Direct";
}

/** Landing path from the captured full URL ("/boiler-repair"), null at "/". */
export function landedPath(landingUrl: string | null): string | null {
  if (!landingUrl) return null;
  try {
    const url = new URL(landingUrl);
    return url.pathname === "/" ? null : url.pathname;
  } catch {
    return null;
  }
}

/** "Just now" / "14m ago" / "3h ago" / "Tue 14:32" / date — calm, compact. */
export function leadTimeLabel(createdAtMs: number, nowMs: number): string {
  const diff = nowMs - createdAtMs;
  const minute = 60_000;
  if (diff < minute) return "Just now";
  if (diff < 60 * minute) return `${Math.floor(diff / minute)}m ago`;
  if (diff < 24 * 60 * minute)
    return `${Math.floor(diff / (60 * minute))}h ago`;
  const date = new Date(createdAtMs);
  if (diff < 7 * 24 * 60 * minute) {
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function leadEventText(event: LeadEvent): string {
  if (event.event_type === "note_added") return event.note ?? "Note added";
  if (event.event_type === "status_change")
    return `Status: ${event.old_value ?? "--"} -> ${event.new_value ?? "--"}`;
  if (event.event_type === "value_set")
    return `Value set to £${event.new_value ?? "0"}`;
  if (event.event_type === "sms_sent")
    return `Text alert sent${event.note ? ` (${event.note})` : ""}`;
  if (event.event_type === "sms_reply")
    return `Reply by text: "${event.note ?? ""}"`;
  if (event.event_type === "conversion_fired")
    return `Conversion reported to ad platform (${event.new_value ?? ""})`;
  return event.event_type;
}

export function Leads({ api }: { api: Api }) {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [valueDraft, setValueDraft] = useState<Record<string, string>>({});
  const [events, setEvents] = useState<Record<string, LeadEvent[]>>({});
  const [eventsLoading, setEventsLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [noteErrors, setNoteErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    api
      .leads()
      .then(({ leads }) => {
        if (!cancelled) setLeads(leads);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const applyUpdate = async (
    lead: Lead,
    payload: { status?: LeadStatus; value?: number | null },
  ) => {
    setSavingId(lead.id);
    try {
      const { lead: next } = await api.updateLead(lead.id, payload);
      setLeads((current) =>
        current
          ? current.map((entry) => (entry.id === next.id ? next : entry))
          : current,
      );
    } catch {
      // keep the previous state visible; the select snaps back on re-render
    } finally {
      setSavingId(null);
    }
  };

  const setStatus = (lead: Lead, status: LeadStatus) => {
    if (status === lead.status) return;
    void applyUpdate(lead, { status });
  };

  const saveValue = (lead: Lead) => {
    const raw = (valueDraft[lead.id] ?? "").replace(/[^\d.]/g, "");
    const value = raw === "" ? null : Number(raw);
    if (value !== null && !Number.isFinite(value)) return;
    void applyUpdate(lead, { value });
  };

  const won = (leads ?? []).filter((lead) => lead.status === "won");
  const wonTotal = won.reduce((total, lead) => total + (lead.value ?? 0), 0);

  const loadEvents = async (id: string) => {
    if (events[id] || eventsLoading[id]) return;
    setEventsLoading((current) => ({ ...current, [id]: true }));
    try {
      const result = await api.leadEvents(id);
      setEvents((current) => ({ ...current, [id]: result.events }));
    } catch {
      setNoteErrors((current) => ({ ...current, [id]: true }));
    } finally {
      setEventsLoading((current) => ({ ...current, [id]: false }));
    }
  };

  const addNote = async (id: string) => {
    const note = (notes[id] ?? "").trim();
    if (!note) return;
    setNoteErrors((current) => ({ ...current, [id]: false }));
    try {
      const { event } = await api.addLeadNote(id, note);
      setEvents((current) => ({
        ...current,
        [id]: [event, ...(current[id] ?? [])],
      }));
      setNotes((current) => ({ ...current, [id]: "" }));
    } catch {
      setNoteErrors((current) => ({ ...current, [id]: true }));
    }
  };

  return (
    <section className="card leads-card">
      <div className="card-head leads-head">
        <div>
          <h2>Leads</h2>
          <p className="muted">
            Every enquiry from your site, with the ad click that brought it.
          </p>
        </div>
        {leads && leads.length > 0 && (
          <div className="leads-tally">
            <span className="leads-tally__item">
              <strong>{leads.length}</strong> enquir
              {leads.length === 1 ? "y" : "ies"}
            </span>
            <span className="leads-tally__item leads-tally__item--won">
              <strong>{won.length}</strong> won
              {wonTotal > 0 ? ` · £${wonTotal.toLocaleString()}` : ""}
            </span>
          </div>
        )}
      </div>

      {error && (
        <p className="muted small">
          Leads could not be loaded — refresh to try again.
        </p>
      )}
      {leads && leads.length === 0 && (
        <div className="leads-empty">
          <p className="leads-empty__title">Your inbox is ready.</p>
          <p className="muted small">
            Share your site or run an ad — enquiries land here the moment a form
            is sent, each carrying the campaign and page it came from.
          </p>
        </div>
      )}

      {leads && leads.length > 0 && (
        <ol className="lead-list">
          {leads.map((lead) => {
            const source = leadSourceLabel(lead.click_id_type, lead.utm_source);
            const landed = landedPath(lead.landing_url);
            const isPaid = source !== "Direct";
            const title = lead.name ?? lead.email ?? lead.phone ?? "Someone";
            return (
              <li
                className={`lead-item${lead.status === "won" ? " lead-item--won" : ""}${lead.status === "spam" ? " lead-item--spam" : ""}`}
                key={lead.id}
              >
                <div className="lead-item__head">
                  <span className="lead-item__name">{title}</span>
                  <time className="lead-item__time">
                    {leadTimeLabel(lead.created_at, Date.now())}
                  </time>
                </div>

                {lead.message && (
                  <p className="lead-item__msg">{lead.message}</p>
                )}

                {(lead.email || lead.phone) &&
                  !(lead.email === title && !lead.phone) && (
                    <div className="lead-item__contact">
                      {lead.email && lead.email !== title && (
                        <a href={`mailto:${lead.email}`}>{lead.email}</a>
                      )}
                      {lead.phone && lead.phone !== title && (
                        <a href={`tel:${lead.phone}`}>{lead.phone}</a>
                      )}
                    </div>
                  )}

                <div className="lead-item__receipts">
                  <span
                    className={`lead-chip${isPaid ? " lead-chip--paid" : ""}`}
                  >
                    via <b>{source}</b>
                  </span>
                  {lead.utm_campaign && (
                    <span className="lead-chip">
                      campaign <b>{lead.utm_campaign}</b>
                    </span>
                  )}
                  {landed && (
                    <span className="lead-chip">
                      landed <b>{landed}</b>
                    </span>
                  )}
                </div>

                <div className="lead-item__actions">
                  <label className="lead-status">
                    <span className="visually-hidden">Status</span>
                    <select
                      className={`lead-status__select lead-status__select--${lead.status}`}
                      disabled={savingId === lead.id}
                      onChange={(event) =>
                        setStatus(lead, event.target.value as LeadStatus)
                      }
                      value={lead.status}
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </label>
                  {lead.status === "won" && (
                    <span className="lead-value">
                      <span className="lead-value__symbol">£</span>
                      <input
                        aria-label="Job value"
                        className="lead-value__input"
                        inputMode="decimal"
                        onBlur={() => saveValue(lead)}
                        onChange={(event) =>
                          setValueDraft((current) => ({
                            ...current,
                            [lead.id]: event.target.value,
                          }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") saveValue(lead);
                        }}
                        placeholder="job value"
                        value={
                          valueDraft[lead.id] ??
                          (lead.value === null ? "" : String(lead.value))
                        }
                      />
                    </span>
                  )}
                  {savingId === lead.id && (
                    <span className="muted small">Saving…</span>
                  )}
                </div>
                <details
                  className="lead-notes"
                  onToggle={(event) => {
                    if (event.currentTarget.open) void loadEvents(lead.id);
                  }}
                >
                  <summary>Notes &amp; history</summary>
                  {eventsLoading[lead.id] && (
                    <p className="muted small">Loading...</p>
                  )}
                  {(events[lead.id]?.length ?? 0) > 0 && (
                    <ol className="lead-notes-list">
                      {events[lead.id]?.map((item) => {
                        const timestamp =
                          typeof item.timestamp === "number"
                            ? item.timestamp
                            : Date.parse(item.timestamp);
                        return (
                          <li key={item.id}>
                            <span>{leadEventText(item)}</span>
                            <time>{leadTimeLabel(timestamp, Date.now())}</time>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <form
                    className="lead-note-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void addNote(lead.id);
                    }}
                  >
                    <input
                      maxLength={2000}
                      onChange={(event) =>
                        setNotes((current) => ({
                          ...current,
                          [lead.id]: event.target.value,
                        }))
                      }
                      placeholder="Add a note..."
                      value={notes[lead.id] ?? ""}
                    />
                    <button className="btn-secondary" type="submit">
                      Add
                    </button>
                  </form>
                  {noteErrors[lead.id] && (
                    <p className="error small">
                      Notes could not be updated. Please try again.
                    </p>
                  )}
                </details>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
